import { Box, Button, PureTextButton, Stack, Text } from '@/theme/components';
import { styled } from '@/theme/index';
import { CopyTooltip } from '@/utils/index';
import { useScreenSize } from '@/utils/useScreenSize';

interface PartnerRedirectProps {
  header: string;
  partnerName: string;
  logo: object;
  body: string;
  cta: string;
  disclaimer: string;
  promoCode?: string;
  selectRewardPartner: () => void;
}

const StackContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'flex-end',
  backgroundColor: theme.palette.customCard?.light || 'initial',
  border: `2px solid ${theme.palette.secondary.light}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'row',
  gap: `${theme.spacing(2)}`,
  padding: `${theme.spacing(2)}`,
  width: 'calc(100vw - 30px)',
  flex: 'none',
  order: 1,
  alignSelf: 'stretch',
  flexGrow: 0,
  cursor: 'pointer',
  [theme.breakpoints.up('sm')]: {
    width: '100%',
  },
}));

const LogoTagContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: 'auto',
}));

export const PartnerRedirect = ({
  partnerName,
  header,
  logo,
  body,
  cta,
  disclaimer,
  promoCode,
  selectRewardPartner,
}: PartnerRedirectProps) => {
  const { isMobile } = useScreenSize();

  return (
    <StackContainer
      role="button"
      onClick={selectRewardPartner}
      data-testid={`${partnerName}-reward`}
    >
      <LogoTagContainer>
        <div data-testid={`${partnerName}-logo`} style={logo} />
      </LogoTagContainer>
      <Stack
        width="100%"
        height="100%"
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
      >
        <Stack width={isMobile ? '100%' : '70%'} height="100%">
          <Text
            variant={isMobile ? 'h5' : 'h1'}
            mb={isMobile ? 0 : 1}
            sx={{ '&:hover': { textDecoration: 'underline' } }}
          >
            {header}
          </Text>
          {!isMobile && (
            <Text
              variant="body1"
              fontSize="20px"
              dangerouslySetInnerHTML={{ __html: body }}
            />
          )}
          <Text
            variant="body2"
            fontSize="12px"
            mt={1}
            sx={{ fontWeight: isMobile ? 'bold' : 'normal' }}
          >
            {disclaimer}
          </Text>
        </Stack>
        <Stack
          width={isMobile ? '100%' : '30%'}
          height="100%"
          display="flex"
          alignItems={isMobile ? 'flex-start' : 'center'}
          justifyContent="center"
        >
          {promoCode && (
            <Stack
              mb={isMobile ? 0 : 1}
              alignItems="center"
              data-testid={`${partnerName}-promocode`}
            >
              <Text fontSize="0.875rem">Use code:</Text>
              <CopyTooltip
                value={promoCode}
                buttonStyles={{ fontSize: '0.875rem' }}
              >
                {promoCode}
              </CopyTooltip>
            </Stack>
          )}
          {isMobile ? (
            <PureTextButton sx={{ p: 0 }} onClick={selectRewardPartner}>
              {cta}
            </PureTextButton>
          ) : (
            <Button
              variant="contained"
              color="success"
              sx={{ width: '185px', borderRadius: '4px' }}
            >
              {cta}
            </Button>
          )}
        </Stack>
      </Stack>
    </StackContainer>
  );
};
